import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as usersApi from '@/api/users.api';

/**
 * Query keys for users
 */
export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: usersApi.UserQueryParams) => [...userKeys.lists(), params] as const,
  details: () => [...userKeys.all, 'detail'] as const,
  detail: (id: string) => [...userKeys.details(), id] as const,
  metrics: (id: string) => [...userKeys.detail(id), 'metrics'] as const,
  current: () => [...userKeys.all, 'current'] as const,
};

/**
 * Fetch users with filters
 */
export function useUsers(params?: usersApi.UserQueryParams) {
  return useQuery({
    queryKey: userKeys.list(params || {}),
    queryFn: () => usersApi.getUsers(params),
  });
}

/**
 * Fetch a single user
 */
export function useUser(id: string) {
  return useQuery({
    queryKey: userKeys.detail(id),
    queryFn: () => usersApi.getUser(id),
    enabled: !!id,
  });
}

/**
 * Fetch current user
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: userKeys.current(),
    queryFn: () => usersApi.getCurrentUser(),
  });
}

/**
 * Fetch user metrics
 */
export function useUserMetrics(id: string) {
  return useQuery({
    queryKey: userKeys.metrics(id),
    queryFn: () => usersApi.getUserMetrics(id),
    enabled: !!id,
  });
}

/**
 * Update user mutation
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: usersApi.UpdateUserData }) =>
      usersApi.updateUser(id, data),
    onSuccess: (updatedUser) => {
      queryClient.invalidateQueries({ queryKey: userKeys.lists() });
      queryClient.setQueryData(userKeys.detail(updatedUser.id), updatedUser);
    },
  });
}

/**
 * Sync users from LDAP mutation
 */
export function useSyncFromLdap() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => usersApi.syncFromLdap(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
